/**
 * tg.js — thin, dependency-free wrapper over window.Telegram.WebApp.
 *
 * Degrades gracefully when window.Telegram is absent (dev in a normal browser):
 *  - initData falls back to VITE_DEV_INITDATA so API calls + screens render;
 *  - colorScheme follows the OS `prefers-color-scheme`;
 *  - MainButton / BackButton / haptic become no-ops;
 *  - LocationManager rejects with 'DESKTOP' (mirrors the real desktop path).
 *
 * Everything the app touches on the Telegram SDK goes through here, so the
 * rest of the code never reads window.Telegram directly.
 */

const WA = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;
const hasTG = !!WA;

const DEV_INITDATA = import.meta.env.VITE_DEV_INITDATA || '';

// ---- identity ---------------------------------------------------------------

/** Raw Telegram initData query string sent as params.init_data on every call. */
export const initData = hasTG ? WA.initData || DEV_INITDATA : DEV_INITDATA;

/** The unsafe user object (display only — never trusted for auth). */
export const user = hasTG ? WA.initDataUnsafe?.user || null : null;

export const isTelegram = hasTG;

// ---- theme ------------------------------------------------------------------

function osDark() {
  return typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** 'light' | 'dark' */
export function colorScheme() {
  if (hasTG && WA.colorScheme) return WA.colorScheme;
  return osDark() ? 'dark' : 'light';
}

/** Telegram themeParams object (bg_color, button_color, …) or {}. */
export function themeParams() {
  return (hasTG && WA.themeParams) || {};
}

// Map a subset of Telegram themeParams onto our design tokens so the app wears
// the user's actual Telegram theme (first-party feel). theme.css supplies the
// thesis defaults; these direct style props override select vars when present.
const TG_VAR_MAP = {
  bg_color: '--paper',
  secondary_bg_color: '--card',
  section_bg_color: '--card',
  text_color: '--ink',
  subtitle_text_color: '--ink-2',
  hint_color: '--ink-3',
  button_color: '--accent',
  button_text_color: '--accent-ink',
  destructive_text_color: '--bad',
};

/** Stamp data-theme + map themeParams onto CSS custom properties on <html>. */
export function applyTheme() {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-theme', colorScheme());
  const tp = themeParams();
  for (const [tgKey, cssVar] of Object.entries(TG_VAR_MAP)) {
    const v = tp[tgKey];
    if (v) root.style.setProperty(cssVar, v);
  }
}

/** Subscribe to Telegram's themeChanged event. Returns an unsubscribe fn. */
export function onThemeChanged(cb) {
  if (!hasTG) {
    // In a plain browser, react to OS scheme flips instead.
    if (typeof window === 'undefined' || !window.matchMedia) return () => {};
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => cb();
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }
  const handler = () => cb();
  WA.onEvent('themeChanged', handler);
  return () => WA.offEvent('themeChanged', handler);
}

// ---- lifecycle --------------------------------------------------------------

export function ready() {
  if (hasTG) WA.ready();
}

export function expand() {
  if (hasTG) WA.expand();
}

export function close() {
  if (hasTG) WA.close();
}

/**
 * Open a t.me / Telegram deep link (e.g. the onboarding bot) from inside the
 * Mini App. Uses WebApp.openTelegramLink when available (stays in Telegram),
 * else falls back to a plain new tab so the not-linked notice still works in a
 * dev browser.
 */
export function openTelegramLink(url) {
  if (hasTG && typeof WA.openTelegramLink === 'function') {
    try { WA.openTelegramLink(url); return; } catch (_e) { /* fall through */ }
  }
  if (typeof window !== 'undefined') {
    try { window.open(url, '_blank'); } catch (_e) { /* best-effort */ }
  }
}

// ---- MainButton -------------------------------------------------------------

export const MainButton = {
  show() { if (hasTG) WA.MainButton.show(); },
  hide() { if (hasTG) WA.MainButton.hide(); },
  setText(text) { if (hasTG) WA.MainButton.setText(text); },
  enable() { if (hasTG) WA.MainButton.enable(); },
  disable() { if (hasTG) WA.MainButton.disable(); },
  showProgress(leaveActive = true) {
    if (hasTG) WA.MainButton.showProgress(leaveActive);
  },
  hideProgress() { if (hasTG) WA.MainButton.hideProgress(); },
  /** Set colors (optional — falls back to theme button color). */
  setParams(params) { if (hasTG) WA.MainButton.setParams(params); },
  /** Register a click handler; returns an unsubscribe fn. */
  onClick(cb) {
    if (!hasTG) return () => {};
    WA.MainButton.onClick(cb);
    return () => WA.MainButton.offClick(cb);
  },
};

// ---- BackButton -------------------------------------------------------------

export const BackButton = {
  show() { if (hasTG) WA.BackButton.show(); },
  hide() { if (hasTG) WA.BackButton.hide(); },
  onClick(cb) {
    if (!hasTG) return () => {};
    WA.BackButton.onClick(cb);
    return () => WA.BackButton.offClick(cb);
  },
};

// ---- Haptics ----------------------------------------------------------------

/**
 * haptic(type)
 *  - 'success' | 'warning' | 'error'  -> notificationOccurred
 *  - 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' -> impactOccurred
 *  - 'selection' -> selectionChanged
 */
export function haptic(type) {
  if (!hasTG || !WA.HapticFeedback) return;
  const h = WA.HapticFeedback;
  try {
    if (type === 'success' || type === 'warning' || type === 'error') {
      h.notificationOccurred(type);
    } else if (type === 'selection') {
      h.selectionChanged();
    } else {
      h.impactOccurred(type || 'medium');
    }
  } catch (_e) {
    /* ignore — haptics are best-effort */
  }
}

// ---- LocationManager (Bot API 8.0) ------------------------------------------

const LM = hasTG ? WA.LocationManager : undefined;

/** init() the LocationManager once; resolves when ready. */
function initLocation() {
  return new Promise((resolve) => {
    if (!LM) return resolve(false);
    if (LM.isInited) return resolve(true);
    try {
      LM.init(() => resolve(true));
    } catch (_e) {
      resolve(false);
    }
  });
}

/**
 * getLocation() -> { lat, lng, accuracy }
 * Throws a plain string error the UI maps to a Rejections state:
 *  - 'DESKTOP'    — no LocationManager (Telegram Desktop / plain browser)
 *  - 'DENIED'     — user refused / access not granted
 *  - 'UNAVAILABLE'— manager present but returned no fix (GPS off / timeout)
 */
export async function getLocation() {
  if (!LM) throw 'DESKTOP';
  const ok = await initLocation();
  if (!ok) throw 'DESKTOP';
  if (LM.isLocationAvailable === false) throw 'UNAVAILABLE';

  const data = await new Promise((resolve) => {
    try {
      LM.getLocation((loc) => resolve(loc));
    } catch (_e) {
      resolve(null);
    }
  });

  if (!data) {
    // null == user denied access OR no fix. Distinguish via the granted flag.
    if (LM.isAccessGranted === false) throw 'DENIED';
    throw 'UNAVAILABLE';
  }
  return {
    lat: data.latitude,
    lng: data.longitude,
    accuracy: data.horizontal_accuracy,
  };
}

/** Deep-link the user to Telegram's location permission settings. */
export function openLocationSettings() {
  if (LM && typeof LM.openSettings === 'function') LM.openSettings();
}

/** True when a real LocationManager exists (mobile Telegram). */
export function hasLocationManager() {
  return !!LM;
}

export default {
  initData,
  user,
  isTelegram,
  colorScheme,
  themeParams,
  applyTheme,
  onThemeChanged,
  ready,
  expand,
  close,
  openTelegramLink,
  MainButton,
  BackButton,
  haptic,
  getLocation,
  openLocationSettings,
  hasLocationManager,
};
